import { useMemo } from "react";
import { useSelector } from "react-redux";

const DEFAULT_LTI_PARAMETERS = {
  lisResultSourcedId: "",
  lisOutcomeServiceUrl: "",
  ltiReturnUrl: "",
  ltiSupportsUrl: false,
  ltiSupportsContentItemIframe: false,
  ltiSupportsContentItemLaunchUrl: false,
  canvasCourseId: "",
  canvasApiDomain: "",
  activityId: "",
  channelId: "",
  autoplay: null, // Defaults to null so the frontend knows it hasn't been set
  zspace: null, // Defaults to null so the frontend knows it hasn't been set
};

/**
 * Converts various types of values to a boolean or undefined.
 * @param {string|number|boolean} value - The value to convert.
 * @returns {boolean|undefined} Returns:
 *   - true: if value is "1", 1, or true"
 *   - false: if value is "0", 0, or false"
 *   - undefined: for any other value
 */
const convertToBoolean = (value) => {
  if (value === "1" || value === 1 || value === true) {
    return true;
  } else if (value === "0" || value === 0 || value === false) {
    return false;
  }
  return undefined;
};

/**
 * Checks if the LTI return types include a specific content type
 * @param {string|null|undefined} types - LTI content return types string
 * @param {string} typeToCheck - Content type to check for
 * @returns {boolean} Whether the type is supported
 */
const supportsContentReturnType = (types, typeToCheck) => {
  return types ? String(types).includes(typeToCheck) : false;
};

/**
 * @typedef {Object} LtiParameters
 * @property {string} lisResultSourcedId - LTI result source ID for grade passback
 * @property {string} lisOutcomeServiceUrl - LTI outcome service URL for grade passback
 * @property {string} ltiReturnUrl - URL to return to after completion
 * @property {boolean} ltiSupportsUrl - Whether URL return type is supported
 * @property {boolean} ltiSupportsContentItemIframe - Whether iframe return type is supported
 * @property {boolean} ltiSupportsContentItemLaunchUrl - Whether LTI launch URL return type is supported
 * @property {string} canvasCourseId - Canvas course ID if available
 * @property {string} canvasApiDomain - Canvas API domain if available
 * @property {string} activityId - Custom activity ID if provided
 * @property {string} channelId - Custom channel ID if provided
 * @property {boolean|undefined|null} autoplay - Whether content should autoplay
 * @property {boolean|undefined|null} zspace - Whether zspace mode is enabled
 */

/**
 * This hook extracts various LTI parameters from the user's LTI data in the Redux store,
 * processes them, and returns them in a structured object.
 *
 * @see {@link LtiParameters} for detailed property information
 * @returns {LtiParameters} An object containing processed LTI parameters
 */
export function useLtiUrlParameters() {
  const lti = useSelector((state) => state.User.lti);

  const ltiParameters = useMemo(() => {
    if (!lti) {
      return DEFAULT_LTI_PARAMETERS;
    }

    const ltiReturnUrl =
      lti.ext_content_return_url || lti.launch_presentation_return_url || "";

    // Extract content return types
    const ltiSupportsUrl = supportsContentReturnType(
      lti.ext_content_return_types,
      "url"
    );
    const ltiSupportsContentItemLaunchUrl = supportsContentReturnType(
      lti.ext_content_return_types,
      "lti_launch_url"
    );
    const ltiSupportsContentItemIframe = supportsContentReturnType(
      lti.ext_content_return_types,
      "iframe"
    );

    // Extract Canvas-specific parameters
    const canvasCourseId = lti.custom_canvas_course_id || "";
    const canvasApiDomain = lti.custom_canvas_api_domain || "";

    // Extract LTI 1.1 grade passback (outcomes) parameters
    const lisResultSourcedId = lti.lis_result_sourcedid || "";
    const lisOutcomeServiceUrl = lti.lis_outcome_service_url || "";

    // Extract LTI custom parameters
    const activityId = lti.custom_activity_id || "";
    const channelId = lti.custom_channel_id || "";

    // Extract LTI custom parameters to boolean
    const autoplay =
      "custom_autoplay" in lti
        ? convertToBoolean(lti.custom_autoplay)
        : undefined; // Property doesn't exist (loaded but missing)
    const zspace =
      "custom_zspace" in lti ? convertToBoolean(lti.custom_zspace) : undefined; // Property doesn't exist (loaded but missing)

    return {
      lisResultSourcedId,
      lisOutcomeServiceUrl,
      ltiReturnUrl,
      ltiSupportsUrl,
      ltiSupportsContentItemIframe,
      ltiSupportsContentItemLaunchUrl,
      canvasCourseId,
      canvasApiDomain,
      activityId,
      channelId,
      autoplay,
      zspace,
    };
  }, [lti]);

  return ltiParameters;
}
